/**
 * Which Firebase folder an imported document is uploaded to.
 *
 * Kept free of Vue and of Firebase so it can be unit tested with plain node
 * (tests/unit/documentImportStoragePath.test.js).
 *
 * Running the app locally should not leave files among the real ones, so the
 * folder follows the API the app is pointed at rather than the build mode:
 * `.env` sends the dev server to http://127.0.0.1:5000, and pointing it at the
 * production API instead -- which .env has commented out ready to use -- should
 * behave like production, because at that point it is production data.
 *
 * Unknown hosts are development on purpose. The mistake worth preventing is a
 * test file landing in the production folder; a real file in the dev folder
 * would only happen if someone added a production host without listing it here.
 */

/** Hosts that mean "this is the real backend". */
const PRODUCTION_API_HOSTS = [
  'api.tripplanner.co.il',
  'tripplanneril-24-44cc2d420c9d.herokuapp.com',
];

/** Unchanged, so every document already uploaded stays where it is. */
const PRODUCTION_FOLDER = 'document_imports';
const DEVELOPMENT_FOLDER = 'document_imports_dev';

/** True only for an API URL whose host is listed above. */
function isProductionApi(rootUrl) {
  if (!rootUrl || typeof rootUrl !== 'string') return false;
  let host;
  try {
    // Compared against the parsed host, never with endsWith: that would make
    // 'api.tripplanner.co.il.evil.test' look like production.
    host = new URL(rootUrl).hostname.toLowerCase();
  } catch (e) {
    return false;
  }
  return PRODUCTION_API_HOSTS.indexOf(host) !== -1;
}

/** The folder imported documents belong in for the API in use. */
function documentImportFolder(rootUrl) {
  return isProductionApi(rootUrl) ? PRODUCTION_FOLDER : DEVELOPMENT_FOLDER;
}

/**
 * A filename safe to put in a storage path.
 *
 * `/` separates folders in a Firebase reference, so a name containing one --
 * or `..` -- could write outside the import folder entirely, over somebody
 * else's object. Keep letters (including Hebrew), digits, dot, dash and
 * underscore; everything else becomes an underscore.
 */
function safeFileName(name) {
  const raw = typeof name === 'string' ? name : '';
  const cleaned = raw
    .replace(/[\\/]+/g, '_')
    .replace(/\.{2,}/g, '.')
    .replace(/[^\p{L}\p{N}._-]+/gu, '_')
    .replace(/^[._-]+/, '')
    .slice(0, 100);
  return cleaned || 'document';
}

/**
 * A random part, so one upload can never land on another's object.
 *
 * The path used to be `<folder>/<millisecond>_<name>`, which two people
 * uploading `voucher.pdf` in the same millisecond would share: the second
 * upload would overwrite the first, break its download token -- and, now that
 * a finished import deletes the files it no longer needs, one person's browser
 * could delete the other person's voucher, since the keep-guard only sees the
 * cards in its own session.
 */
function uploadToken() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    }
  } catch (e) { /* fall through to Math.random */ }
  return `${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * The uploader's timestamp, in the shape VoucherUpload.vue writes:
 * '24_Oct_2026_18:16:13_GMT'. An imported document can become a voucher, and
 * everything that shows a voucher's name strips exactly this stamp -- the
 * attraction and accommodation editors, the viewer modal, and the WhatsApp
 * sender in travelerblog/whatsapp/day_details.py. Writing the same stamp is
 * what lets a traveller see 'booking.pdf' instead of the storage path.
 */
function uploadStamp(now) {
  const date = new Date(now);
  return date.toUTCString().split(', ')[1].split(' ').join('_');
}

/**
 * Where one uploaded document goes: unique per upload, and named so the
 * existing strippers can find the original filename.
 *
 *   document_imports/import_<token>_<stamp>_<name>
 *
 * The random token sits BEFORE the stamp on purpose -- everything up to and
 * including the stamp is what gets stripped, so the token never reaches a
 * person while still keeping two uploads of 'voucher.pdf' apart.
 */
function documentImportObjectPath(folder, fileName, options) {
  const opts = options || {};
  const now = opts.now || Date.now();
  const token = opts.token || uploadToken();
  return `${folder}/import_${token}_${uploadStamp(now)}_${safeFileName(fileName)}`;
}

/**
 * True only for a path inside one of the import folders.
 *
 * The last guard before a delete: whatever else goes wrong, this code must not
 * be able to remove an attraction photo, a profile picture or an accommodation
 * image.
 */
function isDocumentImportPath(path) {
  if (!path || typeof path !== 'string') return false;
  if (path.indexOf('..') !== -1) return false;
  return [PRODUCTION_FOLDER, DEVELOPMENT_FOLDER].some(
    folder => path.startsWith(`${folder}/`) && path.length > folder.length + 1,
  );
}

module.exports = {
  DEVELOPMENT_FOLDER,
  uploadStamp,
  PRODUCTION_API_HOSTS,
  PRODUCTION_FOLDER,
  documentImportFolder,
  documentImportObjectPath,
  isDocumentImportPath,
  isProductionApi,
  safeFileName,
};
